import { cx } from 'class-variance-authority';
import { AnimatePresence, motion } from 'framer-motion';
import { FC, useEffect, useState } from 'react';
interface StackedBarChartProps {
  items: { width: number; color: string; label?: string }[];
  className?: string;
  height?: string;
  animationDuration?: number;
  minItemWidth?: number;
  showAnimation?: boolean;
}
const StackedBarChart: FC<StackedBarChartProps> = ({
  items,
  className,
  height = 'h-14',
  animationDuration = 1.5,
  minItemWidth = 0.5,
  showAnimation = true,
}) => {
  const baseStyles = [height];
  const [normalizedItems, setNormalizedItems] = useState(items);
  useEffect(() => {
    const totalWidth = items.reduce((sum, item) => sum + item.width, 0);
    let adjustedItems = items.map((item) => ({
      ...item,
      width: (item.width / totalWidth) * 100,
    }));
    const smallestWidth = Math.min(...adjustedItems.map((item) => item.width));
    if (smallestWidth < minItemWidth) {
      const deficit = minItemWidth - smallestWidth;
      const remainingWidth = 100 - minItemWidth;
      const remainingItems = adjustedItems.filter(
        (item) => item.width !== smallestWidth,
      );
      const remainingTotalWidth = remainingItems.reduce(
        (sum, item) => sum + item.width,
        0,
      );
      adjustedItems = adjustedItems.map((item) => {
        if (item.width === smallestWidth) {
          return { ...item, width: minItemWidth };
        } else {
          return {
            ...item,
            width: (item.width / remainingTotalWidth) * remainingWidth,
          };
        }
      });
    }
    setNormalizedItems(adjustedItems);
  }, [items, minItemWidth]);
  return (
    <div className={cx('relative w-full overflow-hidden', className)}>
      <div className="relative overflow-hidden">
        <div
          className={`absolute inset-0 ${height} rounded-2xs z-0 overflow-hidden bg-black/10`}
        />
        <div className="relative flex w-full overflow-hidden rounded-2xs">
          {showAnimation ? (
            <AnimatePresence>
              {normalizedItems.map((item, index) => {
                const isOnlyOne = normalizedItems.length === 1;
                const roundedClasses = [];
                if (isOnlyOne) {
                  roundedClasses.push('rounded-2xs');
                } else if (index === 0) {
                  roundedClasses.push('rounded-l-2xs');
                } else if (index === normalizedItems.length - 1) {
                  roundedClasses.push('rounded-r-2xs');
                }
                return (
                  item.width > 0 && (
                    <motion.div
                      key={index}
                      initial={{ width: '0%' }}
                      animate={{ width: `${item.width}%` }}
                      exit={{ width: '0%' }}
                      transition={{
                        duration: animationDuration,
                        ease: 'easeInOut',
                      }}
                      className={cx(
                        ...baseStyles,
                        item.color,
                        'z-10',
                        ...roundedClasses,
                      )}
                    ></motion.div>
                  )
                );
              })}
            </AnimatePresence>
          ) : (
            normalizedItems.map((item, index) => {
              const isOnlyOne = normalizedItems.length === 1;
              const roundedClasses = [];
              if (isOnlyOne) {
                roundedClasses.push('rounded-2xs');
              } else if (index === 0) {
                roundedClasses.push('rounded-l-2xs');
              } else if (index === normalizedItems.length - 1) {
                roundedClasses.push('rounded-r-2xs');
              }
              return (
                item.width > 0 && (
                  <div
                    key={index}
                    style={{ width: `${item.width}%` }}
                    className={cx(
                      ...baseStyles,
                      item.color,
                      'z-10',
                      ...roundedClasses,
                    )}
                  ></div>
                )
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};
export default StackedBarChart;
